import { AlertDialogProps, useToastStore } from '@branch-messenger/willow-ui'
import {
  queryOptions,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { use } from 'react'

import {
  createAccount,
  detectExistingAccount,
  getTerms,
  linkExistingAccount,
  searchRoster,
  sendEmailDeepLinkForPhoneUpdate,
  sendOTPForEmailUpdate,
  sendOTPForPhoneUpdate,
  sendVerificationDeeplink,
  updateAccountEmail,
  updateAccountPhone,
  verifyOrgPasscode,
  verifyWorkerPasscode,
} from '@/api/registration'
import {
  EmailDeepLinkModalContent,
  PhoneOTPModalContent,
} from '@/containers/personalInfo'
import { DialogContext } from '@/context'
import { handleHttpException } from '@/lib/httpException'
import {
  identifyUser,
  sendAnalyticsEvent,
  setUserProperties,
} from '@/lib/mixpanel'
import { useAccountManagementStore } from '@/store/accountManagement/accountManagement'
import { useOrganizationStore } from '@/store/organization/organization'
import { OldIResponse } from '@/types/common'
import {
  CreateAccountRequestBody,
  DetectExistingAccountPayload,
  LinkAccountsRequestBody,
  SearchRosterRequestBody,
  SearchRosterResponse,
  SendDeepLinkVerificationRequest,
  UpdateUserEmailBody,
  UpdateUserPhoneBody,
  VerificationPayload,
} from '@/types/registration'

import { userQueryOptions } from '../auth'
import { VerifyOrgPasscodePayload } from './types'

export const useSendVerificationDeeplink = () => {
  const orgId = useOrganizationStore(state => state.orgId)
  const { showAlert } = use(DialogContext)

  return useMutation({
    mutationFn: async (data: SendDeepLinkVerificationRequest) => {
      const res = await sendVerificationDeeplink(orgId, data)
      return res
    },
    onError: err => {
      const status = (err as AxiosError<OldIResponse>)?.response?.status
      let description = 'There was an error sending the verification link'
      if (status === 404) {
        description = 'Worker not found'
      } else if (status === 409) {
        description = 'Worker is already enrolled'
      }
      showAlert({
        title: 'Error',
        description: description,
        hideCancel: true,
      })
    },
  })
}

export const DETECT_EXISTING_ACCOUNT_QUERY_KEY =
  'DETECT_EXISTING_ACCOUNT_QUERY_KEY'
export const detectExistingAccountQueryOptions = (
  payload: DetectExistingAccountPayload
) =>
  queryOptions({
    queryKey: [DETECT_EXISTING_ACCOUNT_QUERY_KEY],
    queryFn: async () => {
      const res = await detectExistingAccount(payload)
      return res.data
    },
    gcTime: Infinity,
    staleTime: Infinity,
  })

export const useVerifyWorkerPasscode = () => {
  const { showAlert } = use(DialogContext)
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async (data: VerificationPayload) => {
      const res = await verifyWorkerPasscode(data)
      return res.data
    },
    onSuccess: (data, { track_id }) => {
      sendAnalyticsEvent('ONBOARDING', 'verification:workerPasscodeSuccess')
      navigate({
        to: '/setup/check-existing-account',
        search: { track_id, email: data?.email },
      })
    },
    onError: () => {
      sendAnalyticsEvent('ONBOARDING', 'verification:workerPasscodeFailure')

      showAlert({
        title: 'Incorrect Passcode',
        description:
          'Please check your spelling or reach out to your manager for the correct passcode.',
        hideCancel: true,
      })
    },
  })
}

export const useVerifyOrgPasscode = () => {
  const { showAlert } = use(DialogContext)
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async ({
      workerPasscodeRequired,
      ...payload
    }: VerifyOrgPasscodePayload) => {
      const res = await verifyOrgPasscode(payload)
      return res.data
    },
    onSuccess: (data, { workerPasscodeRequired, track_id, orgId }) => {
      sendAnalyticsEvent('ONBOARDING', 'verification:success')
      if (workerPasscodeRequired) {
        return navigate({
          to: '/registration/organizations/$orgId/exchange/workerPasscode',
          params: { orgId },
        })
      }

      navigate({
        to: '/setup/check-existing-account',
        search: { track_id, email: data?.email },
      })
    },
    onError: () => {
      sendAnalyticsEvent('ONBOARDING', 'verification:failure')

      showAlert({
        title: 'Incorrect Passcode',
        description:
          'Please check your spelling or reach out to your manager for the correct passcode.',
        hideCancel: true,
      })
    },
  })
}

export const useLinkExistingAccount = () => {
  const orgId = useOrganizationStore(state => state.orgId)
  const { showAlert } = use(DialogContext)
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (data: LinkAccountsRequestBody) =>
      linkExistingAccount(orgId, data),
    onSuccess: () => {
      navigate({ to: '/account/debit-card' })
    },
    onError: () => {
      showAlert({
        title: 'Account linking failed',
        description:
          'There was an error linking your account. Please contact support if the issue persists.',
        hideCancel: true,
      })
    },
  })
}

export const useCreateAccount = () => {
  const orgId = useOrganizationStore(state => state.orgId)
  const { showAlert } = use(DialogContext)
  const { mutateAsync: searchRoster } = useSearchRoster()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async (data: CreateAccountRequestBody) => {
      const res = await createAccount(orgId, data)
      return res
    },

    onSuccess: async ({ status, data: { user_id } }, payload) => {
      identifyUser(user_id)
      setUserProperties({ $email: payload?.email })
      sendAnalyticsEvent('ONBOARDING', 'create-account:success')

      if (status === 201) {
        let phone_number
        try {
          const result = await searchRoster({
            email: payload.email,
          })
          phone_number = result.phone
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          /* empty */
        }
        return navigate({
          to: '/setup/email-verification',
          state: {
            loginRequestBody: {
              email: payload.email,
              password: payload.password,
              ...(phone_number && { phone_number }),
            },
          },
        })
      }

      navigate({
        to: '/account/debit-card',
      })
    },

    onError: () => {
      sendAnalyticsEvent('ONBOARDING', 'create-account:failure')
      showAlert({
        title: `Account creation failed.`,
        description: `There was an error when creating your account. Please contact support if the issue persists.`,
        hideCancel: true,
      })
    },
  })
}

const GET_TERMS_QUERY = 'get-terms-query'
export const useTerms = (options?: { enabled?: boolean }) => {
  const { enabled = true } = options || {}
  return useQuery({
    queryKey: [GET_TERMS_QUERY],
    queryFn: async () => {
      const res = await getTerms()
      return res.data
    },
    enabled,
  })
}

export const useSendOTPForEmailUpdate = () => {
  const { showAlert } = use(DialogContext)

  return useMutation({
    mutationFn: async () => {
      const res = await sendOTPForEmailUpdate()
      return res
    },

    onSuccess: () => {
      sendAnalyticsEvent('UPDATE', 'email-otp:success')
    },

    onError: error => {
      sendAnalyticsEvent('UPDATE', 'email-otp:failure')
      handleHttpException(error, {
        onHttpError: error => {
          const status = error.response?.status
          if (status === 403) {
            return showAlert({
              title: 'Error',
              description:
                'User does not have verified phone. Please verify phone number and then try again.',
              hideCancel: true,
            })
          }
          showAlert({
            title: 'Error occurred',
            description:
              'An error occurred while sending OTP to your phone number on file. Please try again.',
            hideCancel: true,
          })
        },
      })
    },
  })
}

export const useUpdateAccountEmail = () => {
  return useMutation({
    mutationFn: async (data: UpdateUserEmailBody) => {
      const res = await updateAccountEmail(data)
      return res
    },

    onSuccess: () => {
      sendAnalyticsEvent('UPDATE', 'email:success')
    },

    onError: () => {
      sendAnalyticsEvent('UPDATE', 'email:failure')
    },
  })
}

export const useSendEmailDeepLinkForPhoneUpdate = () => {
  const { showAlert } = use(DialogContext)

  return useMutation({
    mutationFn: async () => {
      const res = await sendEmailDeepLinkForPhoneUpdate()
      return res
    },

    onSuccess: () => {
      sendAnalyticsEvent('UPDATE', 'phone-deeplink:success')
    },

    onError: () => {
      sendAnalyticsEvent('UPDATE', 'phone-deeplink:failure')
      showAlert({
        title: `Invalid phone number.`,
        description: `Please check your phone number and try again.`,
        hideCancel: true,
      })
    },
  })
}

export const useSendPhoneOTP = () => {
  const { showAlert } = use(DialogContext)
  return useMutation({
    mutationFn: async (phone: string) => {
      const res = await sendOTPForPhoneUpdate(phone)
      return res
    },
    onSuccess: () => {
      sendAnalyticsEvent('UPDATE', 'phone-otp:success')
    },
    onError: error => {
      sendAnalyticsEvent('UPDATE', 'phone-otp:failure')
      const errorMessage = (error as AxiosError<OldIResponse>)?.response?.data
        .message
      if (errorMessage === 'Email verification not completed') return

      showAlert({
        title: 'Invalid phone number.',
        description: 'Please check your phone number and try again.',
        hideCancel: true,
      })
    },
  })
}

export const useSendOTPForPhoneUpdate = () => {
  const { showDialog } = use(DialogContext)
  const { mutateAsync: sendOTPForPhoneUpdate } = useSendPhoneOTP()
  const { mutateAsync: sendEmailDeepLinkForPhoneUpdate } =
    useSendEmailDeepLinkForPhoneUpdate()
  const { data: user } = useSuspenseQuery(userQueryOptions)

  return useMutation({
    mutationFn: async (phone: string) => {
      const res = await sendOTPForPhoneUpdate(phone)
      return res
    },
    onSuccess: (_res, phone) => {
      showDialog({
        children: <PhoneOTPModalContent phone={phone} />,
      })
    },
    onError: (error, phone) => {
      const errorMessage = (error as AxiosError<OldIResponse>)?.response?.data
        .message
      if (errorMessage === 'Email verification not completed') {
        return sendEmailDeepLinkForPhoneUpdate(undefined, {
          onSuccess: () => {
            showDialog({
              children: (
                <EmailDeepLinkModalContent email={user.email} phone={phone} />
              ),
            })
          },
        })
      }
    },
  })
}

export const useUpdateAccountPhone = () => {
  const queryClient = useQueryClient()
  const { hideDialog } = use(DialogContext)
  const addToast = useToastStore(state => state.addToast)
  const setEditingItem = useAccountManagementStore(
    state => state.setEditingItem
  )

  return useMutation({
    mutationFn: async (data: UpdateUserPhoneBody) => {
      const res = await updateAccountPhone(data)
      return res
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueryOptions.queryKey })
      sendAnalyticsEvent('UPDATE', 'phone:success')
      hideDialog()
      setEditingItem('none')
      addToast({
        variant: 'success',
        title: 'Phone number successfully updated',
      })
    },
    onError: () => {
      sendAnalyticsEvent('UPDATE', 'phone:failure')
    },
  })
}

export const SEARCH_ROSTER = 'search-roster'

export const useSearchRoster = () => {
  const orgId = useOrganizationStore(state => state.orgId)
  const { showAlert } = use(DialogContext)

  return useMutation({
    mutationKey: [SEARCH_ROSTER, orgId],
    mutationFn: async (data: SearchRosterRequestBody) => {
      const res = await searchRoster(orgId, data)
      return res.data
    },
    onError: error => {
      const genericAlertInfo: AlertDialogProps = {
        title: 'Info not found.',
        description:
          'Please try again or contact your organization for assistance.',
        hideCancel: true,
      }
      void handleHttpException(error, {
        onHttpError: e => {
          switch (e.status) {
            case 429:
              showAlert({
                title: 'Limit Reached.',
                description: `You've reached the maximum number of attempts for today. Please try again after 24 hours.`,
                actionDescription: 'OK',
                hideCancel: true,
              })
              break
            case 409:
              showAlert({
                title: 'Multiple matched records.',
                description:
                  'Your phone or email is linked to multiple worker records. To proceed, your manager or system admin will need to correct duplicate records. Once updated, try again.',
                hideCancel: true,
              })
              break
            default:
              showAlert(genericAlertInfo)
          }
        },
        onOtherError: () => {
          showAlert(genericAlertInfo)
        },
      })
    },
  })
}

export const useSearchRosterMutationState = () => {
  const orgId = useOrganizationStore(state => state.orgId)
  const data = useMutationState({
    filters: { mutationKey: [SEARCH_ROSTER, orgId] },
    select: mutation => {
      return mutation.state.data as SearchRosterResponse
    },
  })
  /*
   * Mutation state stores mutation responses in an array with the latest response at the end.
   * Grab the most recent response as that is the most up-to-date for the user.
   */
  return data[data.length - 1]
}
